/**
 * Talent directory hook — wraps GET /api/users/talents.
 *
 * v2 dropped availability / SME / activeDemands / completedDemands /
 * lastActive concepts (see docs/V2_ARCHITECTURE.md §9). The Talent
 * shape below reflects what's actually stored in the v2 user model
 * plus the user_skills join.
 *
 * Visibility is controlled by `User.visible_in_directory` (talent
 * opt-out) on the backend.
 *
 * Test vs real switch comes from the global TestDataVisibilityContext
 * (exclusive: ON = only test talents, OFF = only real talents).
 */

import { useQuery } from '@tanstack/react-query'
import api from '../lib/api'
import { useTestDataVisibility } from '../context/TestDataVisibilityContext'

export interface Talent {
  id: number
  name: string
  email: string
  role: string
  focus_area_code: string | null
  /** The talent's own focus areas — opt-out, so empty means every FA. Filter and
   *  show these: `focus_area_code` is council membership, NULL for talents. */
  preferred_focus_areas?: string[]
  bio: string | null
  skills: string[]
  visible_in_directory: boolean
  is_test_account: boolean
  github_handle: string | null
  job_title: string | null
  department: string | null
  country: string | null
  applications_in_fa?: { demand_code: string | null; demand_title: string; status: string }[]
  invitations_in_fa?: { demand_code: string | null; demand_title: string }[]
}

export interface TalentListResponse {
  items: Talent[]
}

export interface TalentDirectoryFilters {
  search?: string
  faFilter?: string
  skillFilter?: string
}

export function useTalentDirectory(opts: { enabled?: boolean } = {}) {
  const { showTestData } = useTestDataVisibility()
  return useQuery<TalentListResponse>({
    queryKey: ['talent-directory', { includeTest: showTestData }],
    queryFn: async () => {
      const params: Record<string, string> = {
        include_test: showTestData ? 'true' : 'false',
      }
      const res = await api.get<Talent[]>('/users/talents', { params })
      // Backend returns a bare array; wrap to match existing callers.
      return { items: Array.isArray(res.data) ? res.data : [] }
    },
    staleTime: 30_000,
    enabled: opts.enabled ?? true,
  })
}
